import { FC, useMemo } from 'react';
import { Checkbox, ComboBox, InlineLoading } from '@carbon/react';
import { WarningFilled } from '@carbon/react/icons';
import { useAtom } from 'jotai';
import { debounce } from 'lodash-es';
import {
  ColumnDateFilter,
  FilterSection,
  useDataTableContext,
} from 'components/data-table';
import { createEntitiesAtom, createOperatorAccountsAtom } from './atoms';
import styles from './division-orders-filters.module.scss';

// ─────────────────────────────────────
// Source checkboxes (multi-select; no built-in primitive for checkbox lists
// wired to columns yet — mirror the pattern used by package-library-filters).
// ─────────────────────────────────────

// `value` is the canonical Source string stored on the ES doc and accepted by
// the BE filter (`Query::DivisionOrders::FilterHelpers::ALLOWED_SOURCES`);
// `label` is the human copy. They diverge for `Provisioned` → `Auto-Connected`
// so the UI matches the column cell rendering on the list/flat tables.
const SOURCE_OPTIONS = [
  { value: 'Salesforce', label: 'Salesforce' },
  { value: 'Provisioned', label: 'Auto-Connected' },
] as const;

const SourceCheckboxes: FC = () => {
  const { table } = useDataTableContext();
  const column = table.getColumn('source');
  const selected = (column?.getFilterValue() as string[] | undefined) ?? [];

  const toggle = (value: string, next: boolean) => {
    const nextSet = new Set(selected);
    if (next) nextSet.add(value);
    else nextSet.delete(value);
    const nextArray = Array.from(nextSet);
    column?.setFilterValue(nextArray.length > 0 ? nextArray : undefined);
  };

  return (
    <div className={styles.dropdowns}>
      {SOURCE_OPTIONS.map(({ value, label }) => (
        <Checkbox
          key={value}
          id={`do-source-${value.toLowerCase()}`}
          labelText={label}
          checked={selected.includes(value)}
          onChange={(_e, { checked }) => toggle(value, checked)}
        />
      ))}
    </div>
  );
};

// ─────────────────────────────────────
// Operator typeahead (custom — no out-of-the-box typeahead primitive in
// data-table). Filter value is the operator's salesforceId.
// ─────────────────────────────────────

const OperatorTypeahead: FC = () => {
  const { table } = useDataTableContext();
  const column = table.getColumn('operatorName');

  const { searchAtom } = useMemo(() => createOperatorAccountsAtom(), []);
  const [state, setSearchPayload] = useAtom(searchAtom);
  const results = state?.data?.results ?? [];
  const debouncedSearch = useMemo(
    () => debounce(setSearchPayload, 400),
    [setSearchPayload]
  );

  const filterValue = (column?.getFilterValue() as string | undefined) ?? null;
  const selectedItem = filterValue
    ? (results.find((a) => a.salesforceId === filterValue) ?? null)
    : null;

  return (
    <div className={styles.typeaheadWrapper}>
      <ComboBox
        size="md"
        id="do-operator-filter"
        titleText="Operator"
        items={results}
        placeholder="Search operators..."
        itemToString={(a) => a?.name ?? ''}
        selectedItem={selectedItem}
        invalid={!!state?.error}
        invalidText={state?.error}
        onChange={({ selectedItem: next }) => {
          column?.setFilterValue(next?.salesforceId || undefined);
        }}
        onInputChange={(text) => {
          debouncedSearch((current) => ({
            ...current,
            text,
            pageSize: 20,
          }));
        }}
      />
      {state?.loading && (
        <InlineLoading description="" className={styles.spinner} />
      )}
      {!state?.loading && state?.error && (
        <WarningFilled
          className={styles.errorIcon}
          aria-label={`Failed to load operators: ${state.error}`}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────
// Entity typeahead — mirrors OperatorTypeahead but for Insights entities.
// Filter value is the entityId (number).
// ─────────────────────────────────────

const EntityTypeahead: FC = () => {
  const { table } = useDataTableContext();
  const column = table.getColumn('entityName');

  const { searchAtom } = useMemo(() => createEntitiesAtom(), []);
  const [state, setSearchPayload] = useAtom(searchAtom);
  const results = state?.data?.results ?? [];
  const debouncedSearch = useMemo(
    () => debounce(setSearchPayload, 400),
    [setSearchPayload]
  );

  const filterValue = (column?.getFilterValue() as number | undefined) ?? null;
  const selectedItem = filterValue
    ? (results.find((e) => e.id === filterValue) ?? null)
    : null;

  return (
    <div className={styles.typeaheadWrapper}>
      <ComboBox
        size="md"
        id="do-entity-filter"
        titleText="Insights Entity"
        items={results}
        placeholder="Search entities..."
        itemToString={(e) => e?.name ?? ''}
        selectedItem={selectedItem}
        invalid={!!state?.error}
        invalidText={state?.error}
        onChange={({ selectedItem: next }) => {
          column?.setFilterValue(next?.id ?? undefined);
        }}
        onInputChange={(text) => {
          debouncedSearch((current) => ({
            ...current,
            text,
            pageSize: 20,
            responseMode: 'dropdown',
          }));
        }}
      />
      {state?.loading && (
        <InlineLoading description="" className={styles.spinner} />
      )}
      {!state?.loading && state?.error && (
        <WarningFilled
          className={styles.errorIcon}
          aria-label={`Failed to load entities: ${state.error}`}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────
// Panel
// ─────────────────────────────────────

const DivisionOrdersFilters: FC = () => {
  return (
    <div className={styles.filtersWrapper}>
      <FilterSection title="Date Filters">
        <ColumnDateFilter columnId="dateReceived" label="Date Received" />
        <ColumnDateFilter columnId="createdAt" label="Created At" />
        <ColumnDateFilter columnId="updatedAt" label="Updated At" />
      </FilterSection>

      <FilterSection title="Source">
        <SourceCheckboxes />
      </FilterSection>

      <FilterSection title="Division Order Details">
        <div className={styles.dropdowns}>
          <OperatorTypeahead />
          <EntityTypeahead />
        </div>
      </FilterSection>
    </div>
  );
};

export { DivisionOrdersFilters };
